// client/customer/src/pages/Reserve/ReserveStepHeader.tsx
import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';
import { useI18n } from '../../i18n';

type Props = {
  title: string;
  onBack?: () => void;
  backLabel?: string;
  description?: string;
  step?: number;
  totalSteps?: number;
  right?: React.ReactNode; // 右側に何か置きたい時（Step表示など）
  mode?: 'default' | 'confirm';
};

const ReserveStepHeader: React.FC<Props> = ({
  title,
  onBack,
  backLabel,
  description: _description,
  step,
  totalSteps,
  right,
  mode = 'default',
}) => {
  const { t } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const resolvedBackLabel = backLabel ?? t('flow.back');
  const isConfirm = mode === 'confirm';
  const progress =
    step && totalSteps && totalSteps > 0
      ? `${Math.min(100, Math.max(0, (step / totalSteps) * 100))}%`
      : isConfirm
      ? '100%'
      : '0%';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className="sticky z-30 -mx-1"
      style={{ top: isScrolled ? '52px' : 'var(--reserve-step-top, 132px)' }}
    >
      <div
        className={[
          `overflow-hidden rounded-[20px] border shadow-[0_14px_28px_rgba(10,186,181,0.09)] backdrop-blur-sm transition-all duration-200 ${
            isScrolled ? 'px-2.5 py-2' : 'px-3 py-2.5'
          }`,
          isConfirm ? 'border-emerald-200 bg-white/96' : 'border-[#ccefed] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.96))]',
        ].join(' ')}
      >
        <div className={`flex items-center justify-between gap-2 ${isScrolled ? 'mb-1.5' : 'mb-2'}`}>
          <div
            className={[
              'inline-flex items-center rounded-full px-2 py-[3px] text-[8px] font-semibold uppercase tracking-[0.16em]',
              isConfirm ? 'bg-emerald-50 text-emerald-700' : 'bg-[#dff8f6] text-[#087f7c]',
            ].join(' ')}
          >
            {isConfirm ? t('flow.confirm') : t('flow.bookingFlow')}
          </div>
          {step && totalSteps ? (
            <div
              className={[
                'rounded-full px-2 py-[3px] text-[8px] font-semibold uppercase tracking-[0.14em]',
                isConfirm ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-slate-600 ring-1 ring-[#d4efed]',
              ].join(' ')}
            >
              {t('flow.step', { step, totalSteps })}
            </div>
          ) : null}
        </div>

        <div className={`overflow-hidden rounded-full transition-all duration-200 ${isScrolled ? 'mb-1.5 h-[3px]' : 'mb-2 h-1'} ${isConfirm ? 'bg-emerald-100' : 'bg-[#d8f1ef]'}`}>
          <div
            className={`h-full rounded-full ${isConfirm ? 'bg-emerald-600' : ''}`}
            style={isConfirm ? { width: progress } : { width: progress, background: `linear-gradient(90deg, ${BRAND_TIFFANY_BLUE}, #72d9d5)` }}
          />
        </div>

        <div className="flex items-start justify-between gap-2.5">
          <button
            type="button"
            onClick={onBack}
            className={[
              `flex items-center justify-center transition ${isScrolled ? 'h-7 w-7 rounded-[12px]' : 'h-8 w-8 rounded-[14px]'}`,
              isConfirm
                ? 'text-slate-600 hover:bg-slate-100'
                : 'bg-white text-[#087f7c] ring-1 ring-[#d4efed] hover:bg-[#f1fbfa]',
            ].join(' ')}
            aria-label={resolvedBackLabel}
          >
            <ChevronLeft className={isScrolled ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </button>

          <div className="min-w-0 flex-1 text-center">
            <div
              className={`font-semibold tracking-tight text-slate-900 transition-all duration-200 ${
                isScrolled ? 'mt-0 text-[14px]' : 'mt-0 text-[15px]'
              }`}
            >
              {title}
            </div>
          </div>

          <div className={`flex items-center justify-end text-[10px] text-slate-500 ${isScrolled ? 'min-h-[28px] min-w-[48px]' : 'min-h-[32px] min-w-[56px]'}`}>
            {right ?? <span className="inline-block h-4 w-4" />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReserveStepHeader;
